import { useQuery, QueryOptions, UseQueryOptions } from 'react-query'
import { client } from "../services/api";

import { IFilterData } from "interfaces/FilterProps";
import { FindOptionsOrder, FindOptionsRelations, FindOptionsWhere } from "interfaces/IQuery";

export type IFilterWhereOptions<T> = Array<FindOptionsWhere<T>> | FindOptionsWhere<T>

interface IFilterOptions<T> {
  nots?: { key: string, value: string }[]
  since?: Date
  until?: Date
  page?: number
  notPage?: boolean
  size?: number
  params?: IFilterWhereOptions<T>
  select?: { [key: string]: boolean }
  relations?: FindOptionsRelations<T>
  order?: FindOptionsOrder<T>
}

interface IUseDataListParams<T, H = IFilterData<T>> extends QueryOptions {
  queryKey: string;
  dependency?: unknown[];
  route: string;
  filter?: IFilterOptions<T>
  options?: UseQueryOptions<H>;
}

interface IUseDataParams<T, R> extends Omit<IUseDataListParams<T>, 'options'> {
  options?: UseQueryOptions<R>;
}

export function useDataList<T, H = IFilterData<T>>({ filter, queryKey, dependency, route, options }: IUseDataListParams<T, H>) {
  return useQuery<H>([queryKey, { ...dependency, filter }], () => getDataListService<T, H>({ ...filter, route }), {
    staleTime: 1000 * 60 * 5,
    ...options
  })
}

export function useData<T, R>({ filter, queryKey, dependency, route, options }: IUseDataParams<T, R>) {
  return useQuery<R>({
    queryKey: [queryKey, { ...dependency, filter }],
    queryFn: () => getDataListService<T, R>({ ...filter, route }),
    staleTime: 1000 * 60 * 5,
    ...options,
  })
}

interface IGetDataListServiceParams<T> extends IFilterOptions<T> {
  route: string;
}

export async function getDataListService<T, R>(data: IGetDataListServiceParams<T>): Promise<R> {
  const api = client();

  try {
    const { route, ...filter } = data;

    const response = await api.get<R>(route, {
      params: {
        ...filter,
        relations: JSON.stringify(filter.relations),
        order: JSON.stringify(filter.order),
        params: JSON.stringify(filter.params),
        nots: JSON.stringify(filter.nots),
      }
    });

    return response.data;
  } catch {
    return Promise.reject();
  }
}
